import { useTranslation } from 'react-i18next';
import { FieldDescription, FieldError, fieldIds } from './shared';
import type { FieldProps } from './FieldProps';

/**
 * A checkbox group, not a native `<select multiple>` — a native multi-select
 * listbox needs ctrl/cmd-click to pick more than one option, which does not
 * exist on a touch screen. Matches the AC's "renders correctly on a phone
 * screen" line directly.
 */
export function MultiSelectField({ field, value, error, onChange }: FieldProps) {
  const { t } = useTranslation();
  const { inputId, descriptionId, errorId } = fieldIds(field.path);
  const options = (field.schema.items?.enum ?? []).map(String);
  const selected = Array.isArray(value) ? value.map(String) : [];

  function toggle(option: string, checked: boolean) {
    onChange(checked ? [...selected, option] : selected.filter((o) => o !== option));
  }

  return (
    <fieldset>
      <legend className="mb-1 text-sm font-medium">
        {field.schema.title ?? field.key}
        {field.required ? (
          <span aria-hidden="true" className="text-[var(--color-destructive)]">
            {' '}
            *
          </span>
        ) : null}
      </legend>
      <FieldDescription id={descriptionId} text={field.schema.description} />
      <div id={inputId} className="space-y-1" role="group" aria-describedby={error ? errorId : undefined}>
        {options.length === 0 ? (
          <p className="text-xs text-[var(--color-muted-foreground)]">{t('forms.noOptions')}</p>
        ) : (
          options.map((option) => {
            const optionId = `${inputId}-${option}`;
            return (
              <label key={option} htmlFor={optionId} className="flex items-center gap-2 text-sm">
                <input
                  id={optionId}
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={(event) => toggle(option, event.target.checked)}
                />
                {option}
              </label>
            );
          })
        )}
      </div>
      <FieldError id={errorId} message={error} />
    </fieldset>
  );
}
